// T-465 — Riassunto per destinazione (collettiva). Funzione PURA che riproduce
// l'aggregazione dell'edge `export-piatti-zona` (matrice piatti per destinazione +
// dettaglio per reparto), per la schermata interattiva "Riassunto per destinazione"
// (l'analogo lato smistamento della comanda chef). Offline-first: nessun I/O.
//
// Mantenere allineata a `supabase/functions/export-piatti-zona/index.ts:200-321`:
// se cambia la logica di aggregazione dell'export, aggiornare qui (parità coperta
// dal golden-sample `collettivaDestinationSummary.test.ts`).
import { mealCompositionForDiet, type CatalogLite } from './collettivaMealTemplates.js';
import { matchCollettivaDiet } from './collettivaDiet.js';

/** Sovrainsieme strutturale di `supply_requests.lines[]` (campi extra opzionali). */
export interface DestSummaryLine {
  meal_service?: string | null;
  description?: string | null;
  qty: number;
  menu_label?: string | null;
  letto?: string | null;
  reparto?: string | null;
  notes?: string | null;
  primo?: { label?: string | null } | null;
  secondo?: { label?: string | null } | null;
  contorno?: { label?: string | null } | null;
  frutta?: { label?: string | null } | null;
}
export interface DestSummaryRequest {
  destination_id: string;
  lines: DestSummaryLine[];
}
export interface DestInfo {
  id: string;
  name: string;
  city?: string | null;
}
export interface CatalogRow {
  label: string;
  category: string;
  sort_order: number;
}

export interface SummaryDish {
  label: string;
  name: string;
  qty: number;
}
export interface SummaryCategory {
  category: string;
  dishes: SummaryDish[];
}
export interface SummaryBed {
  letto: string;
  diet: string;
  primo: string;
  secondo: string;
  contorno: string;
  frutta: string;
  notes: string;
}
export interface SummaryWard {
  ward: string;
  service: string;
  beds: SummaryBed[];
}
export interface DestinationSummary {
  destId: string;
  destName: string;
  city: string | null;
  totalPortions: number;
  bedCount: number;
  categories: SummaryCategory[];
  wards: SummaryWard[];
}
export interface SummarizeInput {
  requests: DestSummaryRequest[];
  destinations: DestInfo[];
  catalog: CatalogRow[];
  /** label(normalizzata) → nome ricetta assegnato dal piano; assente = mostra la label grezza. */
  dishNameByLabel?: Record<string, string>;
  /** filtra a un servizio (es. 'pranzo'); assente = tutti. */
  mealFilter?: string | null;
}

const CATEGORY_ORDER = ['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA'];
const ORPHAN_CAT = 'ALTRE RICHIESTE';
const norm = (s: string): string => s.trim().toLowerCase();
const SLOTS = ['primo', 'secondo', 'contorno', 'frutta'] as const;

const catOrder = (c: string): number => {
  const i = CATEGORY_ORDER.indexOf(c);
  if (i >= 0) return i;
  if (c === ORPHAN_CAT) return 999;
  return 500;
};

export function summarizeByDestination(input: SummarizeInput): DestinationSummary[] {
  const mealFilter = input.mealFilter ? norm(input.mealFilter) : null;
  const catByLabel = new Map<string, { category: string; sort: number }>();
  for (const c of input.catalog) catByLabel.set(norm(c.label), { category: c.category, sort: c.sort_order });
  const catalogLite: CatalogLite[] = input.catalog.map((c) => ({ label: c.label, category: c.category }));
  const nameOf = (label: string): string => (input.dishNameByLabel?.[norm(label)] ?? '').trim() || label;

  const byDest = new Map<string, DestSummaryLine[]>();
  for (const r of input.requests) {
    const arr = byDest.get(r.destination_id) ?? [];
    for (const l of Array.isArray(r.lines) ? r.lines : []) arr.push(l);
    byDest.set(r.destination_id, arr);
  }

  const out: DestinationSummary[] = [];
  for (const dest of input.destinations) {
    const lines = byDest.get(dest.id);
    if (!lines || lines.length === 0) continue;

    const dishes = new Map<string, { label: string; category: string; sort: number; qty: number }>();
    const wardMap = new Map<string, SummaryWard>(); // `${ward}::${service}` → gruppo reparto
    let totalPortions = 0;
    let bedCount = 0;

    // categoria del piatto dal catalogo (come l'export); non a catalogo → ALTRE RICHIESTE.
    const addDish = (rawLabel: string, qty: number): void => {
      const label = rawLabel.trim();
      if (!label || qty <= 0) return;
      const key = norm(label);
      const meta = catByLabel.get(key);
      const cur = dishes.get(key);
      if (cur) cur.qty += qty;
      else dishes.set(key, { label, category: meta?.category ?? ORPHAN_CAT, sort: meta?.sort ?? 9999, qty });
      totalPortions += qty;
    };

    for (const l of lines) {
      const service = l.meal_service ? norm(l.meal_service) : 'pranzo';
      if (mealFilter && service !== mealFilter) continue;
      const qty = Number(l.qty) || 0;
      if (qty <= 0) continue;
      const letto = (l.letto ?? '').toString().trim();

      if (!letto) {
        // Riga catalogo: la description è già un piatto.
        addDish((l.description ?? '').toString(), qty);
        continue;
      }

      // Riga-letto: espande la dieta nei piatti di portata (trittico esplicito del
      // preposto ⊕ auto-composizione della dieta filtrata sul catalogo).
      const dietCode = (l.menu_label ?? '').toString().trim() || (matchCollettivaDiet((l.description ?? '').toString()) ?? '');
      const auto = mealCompositionForDiet(dietCode, catalogLite);
      const slots: Record<(typeof SLOTS)[number], string> = {
        primo: (l.primo?.label ?? auto.primo ?? '').toString().trim(),
        secondo: (l.secondo?.label ?? auto.secondo ?? '').toString().trim(),
        contorno: (l.contorno?.label ?? auto.contorno ?? '').toString().trim(),
        frutta: (l.frutta?.label ?? auto.frutta ?? '').toString().trim(),
      };
      const anyDish = SLOTS.some((s) => slots[s] !== '');
      if (anyDish) {
        for (const s of SLOTS) if (slots[s]) addDish(slots[s], qty);
      } else {
        // Dieta senza composizione né trittico → mostra la dieta come riga visibile.
        addDish((l.description ?? '').toString(), qty);
      }

      bedCount += 1;
      const ward = (l.reparto ?? '').toString().trim() || 'Senza reparto';
      const wkey = `${ward}::${service}`;
      let group = wardMap.get(wkey);
      if (!group) {
        group = { ward, service, beds: [] };
        wardMap.set(wkey, group);
      }
      group.beds.push({
        letto,
        diet: (l.description ?? '').toString().trim(),
        primo: slots.primo ? nameOf(slots.primo) : '',
        secondo: slots.secondo ? nameOf(slots.secondo) : '',
        contorno: slots.contorno ? nameOf(slots.contorno) : '',
        frutta: slots.frutta ? nameOf(slots.frutta) : '',
        notes: (l.notes ?? '').toString().trim(),
      });
    }

    const byCat = new Map<string, SummaryDish[]>();
    for (const d of [...dishes.values()].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label, 'it'))) {
      const arr = byCat.get(d.category) ?? [];
      arr.push({ label: d.label, name: nameOf(d.label), qty: d.qty });
      byCat.set(d.category, arr);
    }
    const categories: SummaryCategory[] = [...byCat.entries()]
      .sort((a, b) => catOrder(a[0]) - catOrder(b[0]) || a[0].localeCompare(b[0], 'it'))
      .map(([category, ds]) => ({ category, dishes: ds }));

    const wards: SummaryWard[] = [...wardMap.values()]
      .sort((a, b) => a.ward.localeCompare(b.ward, 'it') || a.service.localeCompare(b.service, 'it'))
      .map((w) => ({
        ...w,
        beds: w.beds.slice().sort((x, y) => x.letto.localeCompare(y.letto, 'it', { numeric: true })),
      }));

    out.push({ destId: dest.id, destName: dest.name, city: dest.city ?? null, totalPortions, bedCount, categories, wards });
  }

  return out;
}
